/**
 * `<ProDocUploadTray>` and `useLeaveWarning` — **an upload carries on when the
 * popup closes** (W22, FR-59, plan D57).
 *
 * The tray sits in the bottom corner while a host app's files are on their way
 * — *4 of 12 stored*, then *12 stored · 3 being read*, then *12 stored · 1
 * failed* — and reopens the popup (`onOpen`) with every row as it was. It is
 * hidden while the popup is open, while nothing is on its way, and once the
 * person has seen how it ended (in the popup, or by its ×). Every count is the
 * batch's own (`upload-queue.ts`); nothing is asked of the service.
 *
 * `useLeaveWarning(ownerApp)` asks the browser to warn before the tab is
 * closed or reloaded while a file is **not yet stored** — a reading in
 * progress goes on in the service whatever the tab does, so it does not count.
 * `uploadsInFlight(ownerApp)` is the same number for a host's own question —
 * the org switch and sign-out ask while it is above nought.
 */
import { Button, Card, TONE_TEXT, cn } from '@fiveox/ui'
import { CircleAlert, CircleCheck, LoaderCircle, X } from 'lucide-react'
import { useEffect } from 'react'

import { dismissFinished, useTray, useUploadsInFlight } from './upload-queue'

export interface ProDocUploadTrayProps {
  /** The host app whose files it counts — the uploader's `ownerApp`. */
  ownerApp: string
  /** Reopen the popup — the rows are the batch's, as they were. */
  onOpen: () => void
  /** One organisation's files only. Omitted, the host app's files in this tab. */
  orgId?: string | null
  className?: string
}

export function ProDocUploadTray({ ownerApp, onOpen, orgId, className }: ProDocUploadTrayProps) {
  const tray = useTray(ownerApp, orgId)
  if (!tray) return null
  const going = tray.phase !== 'done'
  const Icon = going ? LoaderCircle : tray.failed > 0 ? CircleAlert : CircleCheck
  const tone = going ? 'text-primary' : tray.failed > 0 ? TONE_TEXT.warning : TONE_TEXT.success
  const heading = tray.phase === 'uploading' ? 'Uploading' : tray.phase === 'reading' ? 'Being read' : 'Uploads finished'
  return (
    <div className={cn('fixed right-4 bottom-4 z-30 w-[min(22rem,calc(100vw-2rem))]', className)} role="status" aria-live="polite" data-upload-tray={tray.phase}>
      <Card padding="sm" className="shadow-lg">
        <div className="flex items-center gap-2">
          <Icon size={18} className={cn('shrink-0', tone, going && 'animate-spin')} aria-hidden />
          <Button variant="ghost" size="sm" className="h-auto min-w-0 flex-1 flex-col items-start py-1 text-left" onClick={onOpen} aria-label={`${heading}: ${tray.text}. Show the uploads`}>
            <span className="text-foreground text-sm font-medium">{heading}</span>
            <span className="text-muted-foreground truncate text-xs font-normal">{tray.text}</span>
          </Button>
          {!going && (
            <Button variant="ghost" size="sm" aria-label="Dismiss" title="Dismiss" onClick={() => dismissFinished(ownerApp, orgId)}>
              <X size={14} aria-hidden />
            </Button>
          )}
        </div>
        {tray.phase === 'uploading' && (
          <div
            role="progressbar"
            aria-label="Upload progress"
            aria-valuemin={0}
            aria-valuemax={tray.total}
            aria-valuenow={tray.stored}
            className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full"
          >
            <div className="bg-primary h-full rounded-full transition-all duration-300" style={{ width: `${tray.total ? (tray.stored / tray.total) * 100 : 0}%` }} />
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * Warn before the tab closes or reloads while a host app's file is not yet
 * stored (FR-59). The warning is set while there is one and cleared when there
 * is none. Answers how many there are.
 */
export function useLeaveWarning(ownerApp: string): number {
  const inFlight = useUploadsInFlight(ownerApp)
  const warn = inFlight > 0
  useEffect(() => {
    if (!warn) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Older browsers ask only when a value is set; its words are the browser's own.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [warn])
  return inFlight
}
